import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import './toast.css'

interface Akcja {
  etykieta: string
  wykonaj(): void | Promise<void>
}

interface Wpis {
  id: number
  tekst: string
  rodzaj: 'informacja' | 'blad'
  akcja?: Akcja
  /** Dymek w drodze do zniknięcia — dostaje animację wyjścia, zanim zniknie. */
  wychodzi?: boolean
}

interface Api {
  pokaz(tekst: string, akcja?: Akcja): void
  pokazBlad(tekst: string): void
}

const Kontekst = createContext<Api | null>(null)

const CZAS_ZYCIA_MS = 6000
const CZAS_WYJSCIA_MS = 200
/** Więcej dymków naraz to już nie informacja, tylko zasłona. */
const MAKS_WIDOCZNYCH = 3

export function DostawcaToastow({ children }: { children: ReactNode }) {
  const [wpisy, ustawWpisy] = useState<Wpis[]>([])
  const licznik = useRef(0)

  const usun = useCallback((id: number) => {
    // Najpierw wyjście, dopiero potem zdjęcie z listy — inaczej dymek
    // przepadałby w połowie kadru.
    ustawWpisy((poprzednie) =>
      poprzednie.map((wpis) => (wpis.id === id ? { ...wpis, wychodzi: true } : wpis)),
    )
    setTimeout(() => {
      ustawWpisy((poprzednie) => poprzednie.filter((wpis) => wpis.id !== id))
    }, CZAS_WYJSCIA_MS)
  }, [])

  const dodaj = useCallback(
    (tekst: string, rodzaj: Wpis['rodzaj'], akcja?: Akcja) => {
      licznik.current += 1
      const id = licznik.current
      ustawWpisy((poprzednie) => [...poprzednie, { id, tekst, rodzaj, akcja }].slice(-MAKS_WIDOCZNYCH))
      setTimeout(() => usun(id), CZAS_ZYCIA_MS)
    },
    [usun],
  )

  const wartosc = useMemo<Api>(
    () => ({
      pokaz: (tekst, akcja) => dodaj(tekst, 'informacja', akcja),
      pokazBlad: (tekst) => dodaj(tekst, 'blad'),
    }),
    [dodaj],
  )

  return (
    <Kontekst.Provider value={wartosc}>
      {children}
      <div className="toasty" role="status" aria-live="polite">
        {wpisy.map((wpis) => (
          <div
            key={wpis.id}
            className={[
              'toast',
              wpis.rodzaj === 'blad' ? 'toast--blad' : '',
              wpis.wychodzi === true ? 'toast--wychodzi' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span>{wpis.tekst}</span>
            {wpis.akcja !== undefined ? (
              <button
                type="button"
                className="toast__akcja"
                onClick={() => {
                  usun(wpis.id)
                  void wpis.akcja?.wykonaj()
                }}
              >
                {wpis.akcja.etykieta}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </Kontekst.Provider>
  )
}

export function useToast(): Api {
  const api = useContext(Kontekst)
  if (api === null) throw new Error('useToast wymaga DostawcyToastow')
  return api
}
